import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Typography, Box, Button, Divider } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5', py: 4 }}>
      <Container maxWidth="md">
        <Box sx={{ backgroundColor: 'white', borderRadius: 2, p: 4, boxShadow: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/')}
            sx={{ mb: 3 }}
          >
            Back to Home
          </Button>

          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: '#1976d2' }}>
            Privacy Policy
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </Typography>

          <Divider sx={{ mb: 4 }} />

          <Box sx={{ '& h2': { mt: 4, mb: 2, fontWeight: 600 }, '& p': { mb: 2, lineHeight: 1.7 } }}>
            <Typography variant="h5" component="h2">
              1. Introduction
            </Typography>
            <Typography variant="body1" paragraph>
              Welcome to School Portal. We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application and educational platform.
            </Typography>

            <Typography variant="h5" component="h2">
              2. Information We Collect
            </Typography>
            <Typography variant="body1" paragraph>
              We collect information that you provide directly to us, including:
            </Typography>
            <Typography variant="body1" component="div" sx={{ ml: 3, mb: 2 }}>
              • <strong>Personal Information:</strong> Name, email address, phone number, date of birth
              <br />
              • <strong>Educational Information:</strong> Student ID, class information, grades, assignments, attendance records
              <br />
              • <strong>Authentication Data:</strong> Login credentials, security questions
              <br />
              • <strong>Communication Data:</strong> Messages between teachers, parents, and students
              <br />
              • <strong>Usage Data:</strong> App usage patterns, features accessed, time spent on platform
            </Typography>

            <Typography variant="h5" component="h2">
              3. How We Use Your Information
            </Typography>
            <Typography variant="body1" paragraph>
              We use the collected information for the following purposes:
            </Typography>
            <Typography variant="body1" component="div" sx={{ ml: 3, mb: 2 }}>
              • To provide and maintain our educational services
              <br />
              • To manage user accounts and authentication
              <br />
              • To facilitate communication between teachers, parents, and students
              <br />
              • To track academic progress and generate reports
              <br />
              • To send important notifications and updates
              <br />
              • To improve our services and user experience
              <br />
              • To ensure the security and integrity of our platform
            </Typography>

            <Typography variant="h5" component="h2">
              4. Data Sharing and Disclosure
            </Typography>
            <Typography variant="body1" paragraph>
              We do not sell your personal information. We may share your information only in the following circumstances:
            </Typography>
            <Typography variant="body1" component="div" sx={{ ml: 3, mb: 2 }}>
              • <strong>With School Personnel:</strong> Teachers and administrators who need access to perform their educational duties
              <br />
              • <strong>With Parents/Guardians:</strong> Information about students is shared with their registered parents or guardians
              <br />
              • <strong>Legal Requirements:</strong> When required by law, court order, or governmental authority
              <br />
              • <strong>Service Providers:</strong> Third-party vendors who assist in operating our platform (under strict confidentiality agreements)
            </Typography>

            <Typography variant="h5" component="h2">
              5. Data Security
            </Typography>
            <Typography variant="body1" paragraph>
              We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
            </Typography>
            <Typography variant="body1" component="div" sx={{ ml: 3, mb: 2 }}>
              • Encryption of data in transit and at rest
              <br />
              • Regular security assessments and updates
              <br />
              • Access controls and authentication mechanisms
              <br />
              • Employee training on data protection
              <br />
              • Regular backups and disaster recovery procedures
            </Typography>

            <Typography variant="h5" component="h2">
              6. Children's Privacy
            </Typography>
            <Typography variant="body1" paragraph>
              Our service is designed for educational institutions and may be used by children under 13. We comply with the Children's Online Privacy Protection Act (COPPA) and similar regulations. We only collect information from children with verifiable parental consent and use it solely for educational purposes.
            </Typography>

            <Typography variant="h5" component="h2">
              7. Data Retention
            </Typography>
            <Typography variant="body1" paragraph>
              We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. Student records are typically retained according to educational institution policies and applicable regulations.
            </Typography>

            <Typography variant="h5" component="h2">
              8. Your Rights
            </Typography>
            <Typography variant="body1" paragraph>
              You have the right to:
            </Typography>
            <Typography variant="body1" component="div" sx={{ ml: 3, mb: 2 }}>
              • Access your personal information
              <br />
              • Request correction of inaccurate data
              <br />
              • Request deletion of your data (subject to legal requirements)
              <br />
              • Object to processing of your data
              <br />
              • Withdraw consent at any time
              <br />
              • Request a copy of your data in a portable format
            </Typography>

            <Typography variant="h5" component="h2">
              9. Cookies and Tracking
            </Typography>
            <Typography variant="body1" paragraph>
              We use cookies and similar tracking technologies to enhance your experience, analyze usage patterns, and maintain session information. You can control cookie settings through your browser preferences, but disabling cookies may limit functionality.
            </Typography>

            <Typography variant="h5" component="h2">
              10. Third-Party Services
            </Typography>
            <Typography variant="body1" paragraph>
              Our app may contain links to third-party services or integrate with external platforms. We are not responsible for the privacy practices of these third parties. We encourage you to review their privacy policies.
            </Typography>

            <Typography variant="h5" component="h2">
              11. International Data Transfers
            </Typography>
            <Typography variant="body1" paragraph>
              Your information may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place to protect your data in accordance with this Privacy Policy.
            </Typography>

            <Typography variant="h5" component="h2">
              12. Changes to This Privacy Policy
            </Typography>
            <Typography variant="body1" paragraph>
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. Significant changes will be communicated through in-app notifications or email.
            </Typography>

            <Typography variant="h5" component="h2">
              13. Contact Us
            </Typography>
            <Typography variant="body1" paragraph>
              If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us at:
            </Typography>
            <Typography variant="body1" component="div" sx={{ ml: 3, mb: 2 }}>
              <strong>Email:</strong> info@classhero.online
              <br />
              <strong>Phone:</strong> +974 6631 5467
            </Typography>

            <Typography variant="h5" component="h2">
              14. Consent
            </Typography>
            <Typography variant="body1" paragraph>
              By using our application, you consent to the collection and use of information as described in this Privacy Policy. If you do not agree with this policy, please do not use our services.
            </Typography>
          </Box>

          <Divider sx={{ my: 4 }} />

          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="contained"
              onClick={() => navigate('/')}
              sx={{ mt: 2 }}
            >
              Back to Home
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy;
